import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { exhibitionService } from '../services/api';
import ExhibitionGrid from '../components/ExhibitionGrid';
import SearchBar from '../components/SearchBar';
import { SkeletonGrid } from '../components/SkeletonCard';
import ErrorMessage from '../components/ErrorMessage';
import { getUserFacingApiError } from '../utils/apiErrors';
import { useSEO } from '../utils/useSEO';

const HomePage = () => {
  useSEO();
  const navigate = useNavigate();
  const [currentExhibitions, setCurrentExhibitions] = useState([]);
  const [upcomingExhibitions, setUpcomingExhibitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      exhibitionService.getByStatus('current'),
      exhibitionService.getByStatus('upcoming')
    ])
      .then(([current, upcoming]) => {
        setCurrentExhibitions(current.slice(0, 6));
        setUpcomingExhibitions(upcoming.slice(0, 6));
      })
      .catch((err) => {
        console.error('Error fetching exhibitions:', err);
        setError(getUserFacingApiError(err));
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-brand to-brand-dark text-white">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Discover Art & Culture Near You
          </h1>
          <p className="text-xl md:text-2xl text-brand-lighter mb-8">
            Find current and upcoming exhibitions and cultural events.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl">
            <SearchBar 
              placeholder="Search events, artists, venues..."
              onSearch={(query) => {
                if (query) {
                  navigate(`/search?search=${encodeURIComponent(query)}`);
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <ErrorMessage message={error} />}

        {loading ? (
          <>
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                On Now
              </h2>
              <SkeletonGrid count={6} />
            </section>
            <section>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Coming Soon
              </h2>
              <SkeletonGrid count={6} />
            </section>
          </>
        ) : !error && (
          <>
            {/* Current Exhibitions */}
            <section className="mb-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-slate-900">
                  Current Exhibitions
                </h2>
                <Link 
                  to="/search?status=current" 
                  className="text-brand hover:text-brand-dark font-semibold flex items-center gap-1"
                >
                  View all
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              
              {currentExhibitions.length > 0 ? (
                <ExhibitionGrid exhibitions={currentExhibitions} />
              ) : (
                <div className="text-center py-12 text-slate-500">
                  Nothing on right now.
                </div>
              )}
            </section>

            {/* Upcoming Exhibitions */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-slate-900">
                  Coming Soon
                </h2>
                <Link 
                  to="/search?status=upcoming" 
                  className="text-brand hover:text-brand-dark font-semibold flex items-center gap-1"
                >
                  View all
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              
              {upcomingExhibitions.length > 0 ? (
                <ExhibitionGrid exhibitions={upcomingExhibitions} />
              ) : (
                <div className="text-center py-12 text-slate-500">
                  Nothing coming up yet.
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default HomePage;
